import React, { Suspense, lazy } from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import ScrollRestoration from './components/ScrollRestoration';

// Lazy load all page components
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const CoursesPage = lazy(() => import('./pages/CoursesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const StudentVerifierPage = lazy(() => import('./pages/StudentVerifierPage'));
const CourseDetailPage = lazy(() => import('./pages/CourseDetailPage'));
const CertificateVerifyPage = lazy(() => import('./pages/CertificateVerifyPage'));
const NewAdminPage = lazy(() => import('./pages/newAdminPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Global toggle for maintenance mode
const IS_MAINTENANCE_MODE = false;

const MaintenanceGuard = () => {
  if (IS_MAINTENANCE_MODE) {
    return <NotFoundPage />;
  }
  return <Outlet />;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading || user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  // If not logged in and trying to access admin-dashboard, redirect to landing page
  if (!user && location.pathname === "/admin-dashboard") {
    return <Navigate to="/" replace />;
  }

  // Redirect after login to /admin-dashboard if user is at / or /login
  if (user && ["/", "/login"].includes(location.pathname)) {
    return <Navigate to="/admin-dashboard" replace />;
  }

  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="w-12 h-12 border-4 border-purple-900 border-t-purple-500 rounded-full animate-spin"></div>
      </div>
    }>
      <Routes>
        <Route element={<MaintenanceGuard />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/courses/:slug" element={<CourseDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/studentVerify/" element={<StudentVerifierPage />} />
          <Route path="/certificate-verify" element={<CertificateVerifyPage />} />
          <Route path="/admin-dashboard" element={user ? <NewAdminPage /> : <Navigate to="/login" replace />} />
        </Route>
        {/* Catch-all for any other unmatched routes */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  React.useEffect(() => {
    // Disable context menu for native app feel, but allow on inputs
    const handleContextMenu = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
        return;
      }
      e.preventDefault();
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  return (
    <Router>
      <Toaster position="top-right" />
      <ScrollRestoration />
      <AppRoutes />
    </Router>
  );
}

export default App;